var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { appStore } from "../state/store";
export class OnboardingComplete {
    constructor(app) {
        this.app = app;
        this.isLoading = true;
        this.error = null;
        this.analysisResult = null;
    }
    /**
     * Orchestrates the data loading logic.
     * It first attempts a "CACHE READ" from the central store. If data is present (cache hit),
     * it renders immediately. If not (cache miss), it triggers an expensive API call.
     */
    loadOrFetchAnalysis() {
        return __awaiter(this, void 0, void 0, function* () {
            const state = appStore.getState();
            // 1. CACHE READ: Check if the analysis data already exists in the store.
            if (state.user.holisticStoryAnalysis) {
                console.log("[CACHE HIT] Using existing holistic analysis from the store.");
                this.analysisResult = state.user.holisticStoryAnalysis;
                this.isLoading = false;
                this.renderResult(); // Immediately render with cached data
                return; // Stop execution
            }
            // 2. CACHE MISS: If no data, proceed with the API call.
            console.log("[CACHE MISS] No analysis found in store. Fetching from API...");
            yield this.performHolisticAnalysis();
        });
    }
    /**
     * Performs the expensive, one-time data fetching and analysis.
     * On success, it performs a "CACHE WRITE" to the central store.
     */
    performHolisticAnalysis() {
        return __awaiter(this, void 0, void 0, function* () {
            const insightContainer = document.getElementById('insight-container');
            const button = document.getElementById('go-to-dashboard');
            if (insightContainer)
                insightContainer.innerHTML = `<div class="loader"></div><p>Synthesizing your motivational DNA...</p><p>Customizing the site for you</p><p>This could take 30-50 secs</p>`;
            if (button)
                button.disabled = true;
            const { user, knowledgeGraph } = appStore.getState();
            try {
                // Step 1: Client-Side Diagnosis
                const bfi = user.profile.bfiProfile;
                const traits = Object.keys(bfi);
                const sortedTraits = traits.sort((a, b) => bfi[b] - bfi[a]);
                const dominantTraitId = sortedTraits[0];
                const developmentalTraitId = sortedTraits[sortedTraits.length - 1];
                const dominantTraitInfo = knowledgeGraph[dominantTraitId];
                const primaryDisadvantageId = Object.keys(dominantTraitInfo.disadvantages)[0];
                const primaryDisadvantage = dominantTraitInfo.disadvantages[primaryDisadvantageId];
                const mitigationInfo = primaryDisadvantage.mitigatingTraits[developmentalTraitId];
                if (!mitigationInfo)
                    throw new Error("Could not find a developmental path in knowledge graph.");
                const prescribedHabitImplementation = mitigationInfo.implementations[0];
                const isTradeOffScenario = prescribedHabitImplementation.impactModel.totalPotentialAmplification < 0;
                const diagnosticContext = {
                    dominantTrait: { id: dominantTraitId, positiveFraming: dominantTraitInfo.positiveFraming[0] },
                    developmentalTrait: { id: developmentalTraitId },
                    identifiedChallenge: { name: primaryDisadvantage.name },
                    prescribedHabit: prescribedHabitImplementation,
                    isTradeOffScenario: isTradeOffScenario
                };
                // Step 2: Call LLM with the context
                const API_URL = process.env.API_BASE_URL;
                const response = yield fetch(`${API_URL}/api/analyze-stories`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({
                        bfiProfile: user.profile.bfiProfile,
                        sevenStories: user.onboardingInputs.sevenStories,
                        lifePriorities: user.onboardingInputs.lifePriorities,
                        workStyleArchetype: user.profile.workStyleArchetype,
                        belongingSense: user.onboardingInputs.belongingSense,
                        diagnosticContext: diagnosticContext
                    }),
                });
                if (!response.ok)
                    throw new Error(`[${response.statusText}] The AI analysis engine failed.`);
                const data = yield response.json();
                data.diagnosticContext = diagnosticContext;
                // 3. CACHE WRITE: On success, update the store. This "warms" the cache for future navigations.
                appStore.updateHolisticStoryAnalysis(data);
                this.analysisResult = data;
            }
            catch (error) {
                this.error = error.message;
            }
            finally {
                this.isLoading = false;
                this.renderResult(); // Render the final result, whether success or error
            }
        });
    }
    /**
     * Renders the final state (success or error) into the DOM after data is fetched or retrieved from cache.
     */
    renderResult() {
        const insightContainer = document.getElementById('insight-container');
        const button = document.getElementById('go-to-dashboard');
        if (!insightContainer)
            return;
        let contentHtml;
        if (this.error) {
            contentHtml = `<p class="error">${this.error}</p>`;
        }
        else if (this.analysisResult) {
            let { detectedSkills, motivatedPattern, valueAlignment, synthesisMessage } = this.analysisResult;
            synthesisMessage = synthesisMessage
                .replace(/<i>/g, '<strong>')
                .replace(/<\/i>/g, '</strong>');
            const skillsHtml = detectedSkills.map(skill => `
                <div class="skill-tag">
                    <svg class="skill-icon" viewBox="0 0 24 24" fill="currentColor"><path d="M19.48 12.04a3.79 3.79 0 00-2.2-3.15l-1.12-.56a3.81 3.81 0 00-3.16 0l-1.12.56a3.79 3.79 0 00-2.2 3.15l.01.01L12 14.5l2.48-2.45zM12 2a10 10 0 100 20 10 10 0 000-20zm0 17.5a7.5 7.5 0 110-15 7.5 7.5 0 010 15z"></path></svg>
                    <span>${skill}</span>
                </div>
            `).join('');
            contentHtml = `
                <div class="analysis-layout">
                    <div class="main-insight fade-in-up stagger-1">
                        <h4>Your Core Motivational DNA</h4>
                        <p class="motivated-pattern">${motivatedPattern}</p>
                        <p class="narrative-synthesis">${synthesisMessage}</p>
                    </div>
                    <div class="supporting-evidence fade-in-up stagger-2">
                        <div class="evidence-card">
                            <h5>Detected Motivated Skills</h5>
                            <p>These are the skills you naturally excel at and enjoy using.</p>
                            <div class="skill-container">${skillsHtml}</div>
                        </div>
                        <div class="evidence-card">
                            <h5>Strategic Alignment</h5>
                            <p>This pattern is the engine that will drive your #1 Priority:</p>
                            <div class="alignment-link">
                                <span><strong>${valueAlignment.priorityName}</strong></span>
                            </div>
                            <blockquote class="ai-rationale">
                                ${valueAlignment.justification}
                            </blockquote>
                        </div>
                    </div>
                </div>
            `;
        }
        else {
            contentHtml = `<p class="error">An unexpected error occurred.</p>`;
        }
        insightContainer.innerHTML = contentHtml;
        if (button)
            button.disabled = false;
    }
    render() {
        return `
            <div class="card wide-card">
                <div class="onboarding-complete-header">
                    <h2>Onboarding Complete!</h2>
                    <p>Your <strong>Data Asset</strong> is now 'Qualified'. We've synthesized your inputs into a core insight that will guide your journey.</p>
                </div>
                <div id="insight-container" class="insight-container-main">
                    <!-- Async content will be rendered here by renderResult() -->
                </div>
                <div class="button-container">
                    <button id="go-to-dashboard" class="button-primary" disabled>Go to My Dashboard</button>
                </div>
            </div>
        `;
    }
    attachEventListeners() {
        var _a;
        (_a = document.getElementById('go-to-dashboard')) === null || _a === void 0 ? void 0 : _a.addEventListener('click', () => {
            this.app.nextStep();
        });
        // This is the new entry point for the component's data logic.
        this.loadOrFetchAnalysis();
    }
}
